/**
 * Schedule Page - SAMA Premium Wellness Experience
 * Calm, spacious, minimal UI for browsing & booking classes
 * Mobile-first, light-mode design with SAMA brand palette
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, addDays, parseISO, isSameDay } from 'date-fns';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import { getStudios, getSessions, getCreditBalance, createBooking, joinWaitlist, leaveWaitlist, getSessionWaitlist, getMyBookings, getSession, cancelBooking, getMyAllowedClassTypes } from '../lib/api';
import { ChevronLeft, MapPin, MapPin as MapPinIcon, ChevronDown, Loader2, Check, Sun, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import BookingConfirmationModal from '../components/BookingConfirmationModal';
import SuccessOverlay from '../components/SuccessOverlay';
import GuestCheckoutModal from '../components/GuestCheckoutModal';

// Use REACT_APP_BACKEND_URL when set (split-host AWS deploy), fall back to relative.
const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

/* ── time filter definitions ─────────────────────────── */
const TIME_FILTERS = [
  { id: 'full_day',      label: 'Full Day',      minHour: 0,  maxHour: 24 },
  { id: 'morning',       label: 'Morning',       minHour: 0,  maxHour: 12 },
  { id: 'midday',        label: 'Midday',        minHour: 12, maxHour: 15 },
  { id: 'evening',       label: 'Evening',       minHour: 15, maxHour: 19 },
  { id: 'late_evening',  label: 'Late Evening',  minHour: 19, maxHour: 24 },
];

const getDefaultTimeFilter = () => 'full_day';

/* ── 5-day rolling booking window ─────────────────────
 * Members can only book / join-waitlist for sessions starting within the
 * next 5 calendar days (today + 4) in Asia/Kolkata (studio TZ). Admins
 * are exempt and pass through. The backend enforces the same rule —
 * frontend just short-circuits with a friendly toast.
 */
const BOOKING_WINDOW_DAYS = 5;
const STUDIO_TZ = 'Asia/Kolkata';

/** Returns "YYYY-MM-DD" for a Date as observed in Asia/Kolkata. */
function ymdInStudioTZ(date) {
  // en-CA always produces YYYY-MM-DD regardless of locale
  return new Intl.DateTimeFormat('en-CA', { timeZone: STUDIO_TZ }).format(date);
}

/** True iff the given session start (ISO string) falls inside the rolling window. */
function isWithinBookingWindow(startTimeIso) {
  if (!startTimeIso) return true;
  const sessionDate = new Date(startTimeIso);
  if (isNaN(sessionDate.getTime())) return true;
  
  const sessionYmd = ymdInStudioTZ(sessionDate);
  
  // Build list of allowed YMDs starting from today (studio TZ)
  const allowed = new Set();
  const now = new Date();
  for (let i = 0; i < BOOKING_WINDOW_DAYS; i++) {
    const d = new Date(now.getTime() + i * 86400000);
    allowed.add(ymdInStudioTZ(d));
  }
  return allowed.has(sessionYmd);
}

const BOOKING_WINDOW_TOAST = `Pre-booking of classes can be done upto ${BOOKING_WINDOW_DAYS} days in advance. Please check back later to book classes for this day/date.`;

/* ── class type helpers ──────────────────────────────── */
const getClassSubtext = (classType) => {
  const parts = [];
  if (classType?.category) parts.push(classType.category);
  if (classType?.duration_mins) parts.push(`${classType.duration_mins} mins`);
  return parts.join(' \u00B7 ') || '';
};

/* ── component ───────────────────────────────────────── */
export default function Schedule() {
  const { member, isLoading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // recommender params
  const recommenderMode   = searchParams.get('mode') === 'guest';
  const recommenderClass  = searchParams.get('class') || '';
  const recommenderStudio = searchParams.get('studio') || '';
  const recommenderRange  = searchParams.get('range') || '';
  const preSelectedSessionId = searchParams.get('session');

  // core state
  const [studios, setStudios]             = useState([]);
  // Class-type overlay map — cover image + short description keyed by
  // class_type_id. Pulled once from /public/class-type-content so we
  // don't slam the API on every session card render.
  const [classTypeOverlays, setClassTypeOverlays] = useState({});
  const [selectedStudio, setSelectedStudio] = useState('');
  const [selectedDate, setSelectedDate]   = useState(new Date());
  const [sessions, setSessions]           = useState([]);
  const [isLoading, setIsLoading]         = useState(true);
  const [creditBalance, setCreditBalance] = useState(0);
  const [bookingInProgress, setBookingInProgress] = useState(null);
  const [waitlistInProgress, setWaitlistInProgress] = useState(null);
  const [waitlistPositions, setWaitlistPositions] = useState({});
  const [selectedTimeFilter, setSelectedTimeFilter] = useState(getDefaultTimeFilter());
  const [showStudioSheet, setShowStudioSheet] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/public/class-type-content`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setClassTypeOverlays(d?.by_id || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Instructor Bio / Class "Know More" popups
  const [viewBioInstructorId, setViewBioInstructorId] = useState(null);
  const [knowMoreClassId, setKnowMoreClassId] = useState(null);

  // Cross-component bridge: BookingConfirmationModal dispatches this
  // when the "View Bio" chip inside it is tapped. Keeps the modal
  // self-contained without prop-drilling a callback down.
  useEffect(() => {
    const handler = (e) => {
      const id = e.detail?.instructor_id;
      if (id) setViewBioInstructorId(id);
    };
    window.addEventListener('sama:open-instructor-bio', handler);
    return () => window.removeEventListener('sama:open-instructor-bio', handler);
  }, []);

  // recommender state
  const [classFilter, setClassFilter]     = useState(recommenderClass);
  const [showAllDates, setShowAllDates]   = useState(recommenderRange === '7d');

  // booking modal
  const [selectedSession, setSelectedSession] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  // Non-preferred-studio confirmation — shown ONCE before the booking
  // modal when a member tries to book at a studio that isn't their
  // preferred one. Skipped for admins, guests, and members with no
  // preferred studio on file. Cleared once acknowledged for the
  // current attempt; we don't persist "don't ask again" so the gentle
  // nudge appears every time a non-preferred-studio booking is made.
  const [studioMismatchSession, setStudioMismatchSession] = useState(null);
  const [bookedSessionIds, setBookedSessionIds] = useState(new Set());
  const [bookedSessionMap, setBookedSessionMap] = useState({}); // session_id -> {booking_id, credits_charged, start_time}
  const [cancelInProgress, setCancelInProgress] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(null);
  // Waitlist cancellation confirm — holds the session object that the user
  // clicked. Spec wants a modal "Cancel Waitlist?" with Confirm / Cancel.
  const [showWaitlistCancelConfirm, setShowWaitlistCancelConfirm] = useState(null);
  const [waitlistCancelInProgress, setWaitlistCancelInProgress] = useState(null);
  // Membership Start Date block — set by the catch block when the backend
  // returns a 403 with code='membership_not_started'. Holds the friendly
  // message so the modal can render it as-is (already includes the date).
  const [membershipBlock, setMembershipBlock] = useState(null);
  const [cancelWindowHours, setCancelWindowHours] = useState(12); // session object

  // Package format-restriction — set once on mount. Shape:
  //   { restricted: bool, class_type_ids: [uuid, ...], package_names: [...] }
  // `restricted=false` means universal (no card is greyed out). When
  // restricted, class-type ids NOT in the allow-list get the "not in
  // your package" grey-out treatment on their booking CTA.
  const [packageRestriction, setPackageRestriction] = useState({ restricted: false, class_type_ids: [], package_names: [] });

  // success overlay
  const [showSuccess, setShowSuccess]     = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // guest checkout
  const [showGuestCheckout, setShowGuestCheckout] = useState(false);
  const [guestSession, setGuestSession]   = useState(null);

  const [preSelectedHandled, setPreSelectedHandled] = useState(false);

  const dates = Array.from({ length: 21 }, (_, i) => addDays(new Date(), i));
  const dateScrollRef = useRef(null);

  /* ── data loading ───────────────────────────────────── */
  const loadBookedSessions = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const bookings = await getMyBookings('confirmed');
      const confirmed = (bookings || []).filter(b => b.status === 'confirmed');
      const ids = new Set(confirmed.map(b => b.session?.id || b.session_id));
      const map = {};
      confirmed.forEach(b => {
        const sid = b.session?.id || b.session_id;
        map[sid] = {
          booking_id: b.booking_id,
          credits_charged: b.credits_charged,
          start_time: b.session?.start_time,
        };
      });
      setBookedSessionIds(ids);
      setBookedSessionMap(map);
    } catch (_) {}
  }, [isAuthenticated]);

  useEffect(() => {
    // Wait until auth has resolved before deciding the default studio so we
    // can honour `member.preferred_studio_id` on the very first render
    // (otherwise the auth-async-arrives-later race would leave us stuck on
    // the FD1 fallback).
    if (authLoading) return;
    async function init() {
      try {
        const studioData = await getStudios();
        setStudios(studioData);
        // Studio selection priority (only applied on initial load, i.e. when
        // selectedStudio is empty — we never override a studio the user has
        // already manually picked via the bottom-sheet selector):
        //   1. ?studio=<id> URL param (recommender flow) — explicit user intent
        //   2. member.preferred_studio_id from profile (must exist in the
        //      visible studio list — i.e. still active/published)
        //   3. FD1 studio if present
        //   4. First studio in the list
        setSelectedStudio((prev) => {
          if (prev) return prev; // already selected — don't override
          const preferredId = member?.preferred_studio_id;
          if (recommenderStudio && studioData.some(s => s.id === recommenderStudio)) {
            return recommenderStudio;
          }
          if (preferredId && studioData.some(s => s.id === preferredId)) {
            return preferredId;
          }
          const fd1 = studioData.find(s => s.name?.includes('FD1'));
          return fd1?.id || studioData[0]?.id || '';
        });
        if (isAuthenticated) {
          const bal = await getCreditBalance().catch(() => ({ balance: 0 }));
          setCreditBalance(bal?.balance || 0);
          loadBookedSessions();
          // Fetch the format-restriction summary for the current member.
          // If it fails (e.g. before migration 031 is applied), leave the
          // default `restricted:false` in place — universal for everyone.
          getMyAllowedClassTypes()
            .then(r => setPackageRestriction({
              restricted:     !!r?.restricted,
              class_type_ids: Array.isArray(r?.class_type_ids) ? r.class_type_ids : [],
              package_names:  Array.isArray(r?.package_names) ? r.package_names : [],
            }))
            .catch(() => { /* non-fatal */ });
        }
      } catch (_) {
        toast.error('Failed to load studios');
      }
    }
    init();
  }, [authLoading, isAuthenticated, loadBookedSessions, recommenderStudio, member?.preferred_studio_id]);

  useEffect(() => {
    fetch(`${API_BASE}/booking-settings/cancellation-policy`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCancelWindowHours(d.cancellation_window_hours); })
      .catch(() => {});
  }, []);


  useEffect(() => {
    async function load() {
      if (!selectedStudio) return;
      setIsLoading(true);
      try {
        if (showAllDates && recommenderRange === '7d') {
          const all = [];
          for (let i = 0; i < 7; i++) {
            const d = addDays(new Date(), i);
            const data = await getSessions(selectedStudio, format(d, 'yyyy-MM-dd'), 1);
            all.push(...data.filter(s => isSameDay(parseISO(s.start_time), d) && !s.class_type?.is_private));
          }
          all.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
          setSessions(all);
        } else {
          const dateStr = format(selectedDate, 'yyyy-MM-dd');
          const data = await getSessions(selectedStudio, dateStr, 1);
          const filtered = data.filter(s => isSameDay(parseISO(s.start_time), selectedDate) && !s.class_type?.is_private);
          filtered.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
          setSessions(filtered);
        }
      } catch (_) {
        setSessions([]);
      } finally {
        setIsLoading(false);
      }
    }
    if (selectedStudio) load();
  }, [selectedStudio, selectedDate, showAllDates, recommenderRange]);

  // pre-selected session from URL
  useEffect(() => {
    async function handle() {
      if (!preSelectedSessionId || preSelectedHandled || isLoading) return;
      let s = sessions.find(x => x.id === preSelectedSessionId);
      if (!s) try { s = await getSession(preSelectedSessionId); } catch (_) { setPreSelectedHandled(true); return; }
      if (s) {
        if (isAuthenticated) {
          const isAdmin = Boolean(member?.is_admin) || member?.role === 'admin';
          if (bookedSessionIds.has(s.id)) toast.info('You have already booked this class');
          else if (
            !isAdmin &&
            member?.preferred_studio_id &&
            (s.studio_id || s.studio?.id) &&
            (s.studio_id || s.studio?.id) !== member.preferred_studio_id
          ) {
            // Preserve the URL→booking-modal handoff, but route through
            // the non-preferred-studio nudge first.
            setStudioMismatchSession(s);
          }
          else { setSelectedSession(s); setShowBookingModal(true); }
        } else { setGuestSession(s); setShowGuestCheckout(true); }
        setPreSelectedHandled(true);
        searchParams.delete('session');
        setSearchParams(searchParams, { replace: true });
      }
    }
    handle();
  }, [preSelectedSessionId, sessions, preSelectedHandled, isLoading, isAuthenticated, bookedSessionIds, searchParams, setSearchParams, member]);

  // waitlist positions
  useEffect(() => {
    if (!isAuthenticated || !sessions.length) return;
    (async () => {
      const pos = {};
      for (const s of sessions.filter(x => x.available_spots <= 0)) {
        try {
          const r = await getSessionWaitlist(s.id);
          if (r.user_position) pos[s.id] = r.user_position;
        } catch (_) {}
      }
      if (Object.keys(pos).length) setWaitlistPositions(pos);
    })();
  }, [sessions, isAuthenticated]);

  /* ── filtered sessions ──────────────────────────────── */
  const filteredSessions = useMemo(() => {
    let list = sessions;
    // class name filter (recommender)
    if (classFilter) {
      const f = classFilter.toLowerCase();
      list = list.filter(s => (s.class_type?.name || '').toLowerCase().includes(f));
    }
    // time filter (skip for full_day)
    if (selectedTimeFilter !== 'full_day') {
      const tf = TIME_FILTERS.find(t => t.id === selectedTimeFilter);
      if (tf) {
        list = list.filter(s => {
          const h = parseISO(s.start_time).getHours();
          return h >= tf.minHour && h < tf.maxHour;
        });
      }
    }
    return list;
  }, [sessions, classFilter, selectedTimeFilter]);

  /* ── handlers ───────────────────────────────────────── */
  const memberIsAdmin = Boolean(member?.is_admin) || member?.role === 'admin';

  // True when the member has a preferred studio on file AND the session
  // (or current studio selection) belongs to a DIFFERENT studio. The
  // gentle confirmation is shown once before opening the booking modal
  // so members don't accidentally book at the wrong location after
  // toggling the studio bottom-sheet. Admins are exempted to avoid
  // operational friction.
  const needsStudioMismatchConfirm = (session) => {
    if (!isAuthenticated || memberIsAdmin) return false;
    const preferred = member?.preferred_studio_id;
    if (!preferred) return false;
    const sessionStudioId =
      session?.studio_id || session?.studio?.id || selectedStudio;
    if (!sessionStudioId) return false;
    return sessionStudioId !== preferred;
  };

  const openBookingModalForSession = (session) => {
    setSelectedSession(session);
    setShowBookingModal(true);
  };

  const handleClassClick = (session) => {
    if (isAuthenticated && bookedSessionIds.has(session.id)) {
      setShowCancelConfirm(session);
      return;
    }
    if (!memberIsAdmin && !isWithinBookingWindow(session.start_time)) {
      toast.info(BOOKING_WINDOW_TOAST, { duration: 5000 });
      return;
    }
    if (!isAuthenticated) { setGuestSession(session); setShowGuestCheckout(true); return; }
    if (needsStudioMismatchConfirm(session)) {
      setStudioMismatchSession(session);
      return;
    }
    openBookingModalForSession(session);
  };

  const handleConfirmBooking = async () => {
    if (!selectedSession) return;
    // Modal is reused for waitlist sign-ups. If the selected session is
    // already full, route through the waitlist API instead of trying to
    // create a booking (the API would reject the booking anyway, but
    // this gives the user a clean, predictable flow with no flash of
    // "insufficient credits" copy).
    const available =
      typeof selectedSession.available_spots === 'number'
        ? selectedSession.available_spots
        : (selectedSession.capacity || 0) - (selectedSession.booked_count || 0);
    if (available <= 0) {
      // Profile completeness gate for waitlist joins too.
      if (member && member.profile_complete === false && !member.is_admin) {
        setShowBookingModal(false);
        toast.error('Please complete your profile before joining a waitlist.');
        navigate(`/account-preferences?return=${encodeURIComponent(window.location.pathname + window.location.search)}`);
        return;
      }
      setBookingInProgress(selectedSession.id);
      try {
        const r = await joinWaitlist(selectedSession.id);
        setWaitlistPositions(prev => ({ ...prev, [selectedSession.id]: r.position }));
        toast.success(`Added to waitlist — position ${r.position}`);
        setShowBookingModal(false);
        setSelectedSession(null);
      } catch (e) {
        if (e?.payload?.code === 'membership_not_started') {
          setShowBookingModal(false);
          setSelectedSession(null);
          setMembershipBlock(e.payload.message);
        } else {
          toast.error(e.message || 'Failed to join waitlist');
        }
      } finally {
        setBookingInProgress(null);
      }
      return;
    }
    const cost = selectedSession.class_type?.credit_cost || 2;
    if (creditBalance < cost) { toast.error(`Insufficient credits.`); setShowBookingModal(false); navigate('/wallet'); return; }
    // Profile completeness gate — booking is blocked backend-side, but
    // we intercept here so the user gets a friendly CTA instead of an
    // opaque 403.
    if (member && member.profile_complete === false && !member.is_admin) {
      setShowBookingModal(false);
      toast.error('Please complete your profile before booking classes.');
      navigate(`/account-preferences?return=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    setBookingInProgress(selectedSession.id);
    try {
      const key = `book_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const result = await createBooking(selectedSession.id, key);
      setShowBookingModal(false);
      setCreditBalance(prev => prev - result.credits_charged);
      setBookedSessionIds(prev => new Set([...prev, selectedSession.id]));
      setBookingInProgress(null);
      setSelectedSession(null);
      navigate(`/booking-confirmed?booking=${result.booking_id}`);
      return;
    } catch (e) {
      if (e?.payload?.code === 'membership_not_started') {
        setShowBookingModal(false);
        setSelectedSession(null);
        setMembershipBlock(e.payload.message);
      } else {
        toast.error(e.message || 'Booking failed');
      }
    }
    finally { setBookingInProgress(null); setSelectedSession(null); }
  };

  const handleJoinWaitlist = async (session) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (!memberIsAdmin && !isWithinBookingWindow(session.start_time)) {
      toast.info(BOOKING_WINDOW_TOAST, { duration: 5000 });
      return;
    }
    setWaitlistInProgress(session.id);
    try {
      const r = await joinWaitlist(session.id);
      toast.success(`Added to waitlist — position ${r.position}`);
      setWaitlistPositions(prev => ({ ...prev, [session.id]: r.position }));
    } catch (e) {
      if (e?.payload?.code === 'membership_not_started') {
        setMembershipBlock(e.payload.message);
      } else {
        toast.error(e.message || 'Failed to join waitlist');
      }
    }
    finally { setWaitlistInProgress(null); }
  };

  const handleLeaveWaitlist = async (session) => {
    if (!session) return;
    setWaitlistCancelInProgress(session.id);
    try {
      await leaveWaitlist(session.id);
      // Drop our own position locally. The backend has already
      // re-sequenced everyone behind us, but other members'
      // positions in `waitlistPositions` aren't ours to mutate —
      // they'll get fresh numbers on their next page load. Their
      // server-side ordering is correct regardless.
      setWaitlistPositions(prev => {
        const next = { ...prev };
        delete next[session.id];
        return next;
      });
      setShowWaitlistCancelConfirm(null);
      toast.success('Waitlist cancelled', {
        description: 'You have been removed from the waitlist for this class.',
      });
    } catch (e) {
      toast.error(e.message || 'Failed to cancel waitlist');
    } finally {
      setWaitlistCancelInProgress(null);
    }
  };

  const handleCancelBooking = async (session) => {
    const info = bookedSessionMap[session.id];
    if (!info) return;
    setCancelInProgress(session.id);
    try {
      const result = await cancelBooking(info.booking_id);
      toast.success(`Booking cancelled. ${result.credits_refunded} credit${result.credits_refunded !== 1 ? 's' : ''} refunded.`);
      setBookedSessionIds(prev => { const next = new Set(prev); next.delete(session.id); return next; });
      setBookedSessionMap(prev => { const next = { ...prev }; delete next[session.id]; return next; });
      setCreditBalance(prev => prev + (result.credits_refunded || 0));
      setShowCancelConfirm(null);
      // Refresh sessions
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const data = await getSessions(selectedStudio, dateStr, 1);
      setSessions(data.filter(s => isSameDay(parseISO(s.start_time), selectedDate) && !s.class_type?.is_private).sort((a, b) => new Date(a.start_time) - new Date(b.start_time)));
    } catch (e) { toast.error(e.message || 'Failed to cancel booking'); }
    finally { setCancelInProgress(null); }
  };

  const clearRecommenderFilters = () => { setClassFilter(''); setShowAllDates(false); setSearchParams({}); };

  const currentStudio = studios.find(s => s.id === selectedStudio);
  const hasRecommenderFilters = classFilter || (showAllDates && recommenderRange === '7d');

  /* ── loading skeleton ───────────────────────────────── */
  if (authLoading) {
    return (
      <div className="min-h-screen bg-sama-cream p-6">
        <Skeleton className="h-10 w-48 mb-8 bg-sama-light-sage/60 rounded-xl" />
        <div className="flex gap-4 mb-8">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-16 w-16 rounded-2xl bg-sama-light-sage/40" />)}</div>
        <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full rounded-2xl bg-sama-light-sage/30" />)}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sama-cream pb-24" data-testid="schedule-page">

      {/* ── Recommender Banner ────────────────────────── */}
      {hasRecommenderFilters && (
        <div className="bg-sama-green/90 text-white px-5 py-3 flex items-center justify-between">
          <p className="text-sm">
            Showing {classFilter ? `"${classFilter}"` : 'recommended'} sessions
            {showAllDates && ' for 7 days'}
          </p>
          <button onClick={clearRecommenderFilters} className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── Header ────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-sama-cream/80 backdrop-blur-xl border-b border-sama-light-sage/40">
        <div className="px-5 py-4 flex items-center justify-between">
          {/* Back */}
          <button
            onClick={() => navigate('/')}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-sama-light-sage/40 transition-colors"
            data-testid="schedule-back-btn"
          >
            <ChevronLeft className="w-5 h-5 text-sama-charcoal" />
          </button>

          {/* Studio Selector Pill */}
          <button
            onClick={() => setShowStudioSheet(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-sama-light-sage shadow-soft hover:shadow-card transition-all"
            data-testid="studio-selector"
          >
            <MapPin className="w-4 h-4 text-sama-sage" />
            <span className="font-medium text-sama-charcoal text-sm">{currentStudio?.name || 'Select Studio'}</span>
            <ChevronDown className="w-4 h-4 text-sama-gray" />
          </button>

          {/* Subtle icon */}
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-sama-light-sage/30">
            <Sun className="w-5 h-5 text-sama-sage" />
          </div>
        </div>
      </header>

      {/* ── Studio Bottom Sheet ───────────────────────── */}
      {showStudioSheet && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 animate-fade-in"
            onClick={() => setShowStudioSheet(false)}
          />
          {/* Sheet */}
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-elevated animate-slide-up max-h-[60vh] overflow-y-auto" data-testid="studio-sheet">
            <div className="p-6">
              {/* Handle */}
              <div className="w-10 h-1 bg-sama-light-sage rounded-full mx-auto mb-6" />
              <h3 className="font-heading text-xl text-sama-charcoal mb-1">Choose Your Studio</h3>
              <p className="text-sm text-sama-gray mb-6">Select where you'd like to practice</p>
              <div className="space-y-2">
                {studios.map((studio) => (
                  <button
                    key={studio.id}
                    onClick={() => { setSelectedStudio(studio.id); setShowStudioSheet(false); }}
                    className={`w-full text-left px-5 py-4 rounded-2xl transition-all ${
                      selectedStudio === studio.id
                        ? 'bg-sama-green text-white shadow-button'
                        : 'bg-sama-cream hover:bg-sama-light-sage/40 text-sama-charcoal'
                    }`}
                    data-testid={`studio-option-${studio.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <MapPin className={`w-4 h-4 ${selectedStudio === studio.id ? 'text-white/80' : 'text-sama-sage'}`} />
                      <div>
                        <p className="font-medium">{studio.name}</p>
                        {studio.city && <p className={`text-xs mt-0.5 ${selectedStudio === studio.id ? 'text-white/70' : 'text-sama-gray'}`}>{studio.city}</p>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Content ───────────────────────────────────── */}
      <div className="px-5 pt-8">
        {/* Section heading */}
        <h2 className="font-heading text-2xl text-sama-charcoal mb-6" data-testid="schedule-heading">
          {isSameDay(selectedDate, new Date()) ? "Today's Practice" : format(selectedDate, 'EEEE, MMM d')}
        </h2>

        {/* ── Date Selector (21 days, smooth scroll) ──── */}
        <div ref={dateScrollRef} className="flex gap-2 overflow-x-auto scrollbar-hide pb-4 -mx-1 px-1 scroll-smooth">
          {dates.map((date, index) => {
            const isSelected = isSameDay(date, selectedDate);
            const isToday = isSameDay(date, new Date());
            const showMonth = index === 0 || date.getDate() === 1;
            return (
              <React.Fragment key={date.toISOString()}>
                {showMonth && (
                  <div className="flex-shrink-0 flex items-center pr-1">
                    <span className="text-[10px] font-semibold text-sama-sage/70 uppercase tracking-widest" style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)' }}>
                      {format(date, 'MMM')}
                    </span>
                  </div>
                )}
                <button
                  onClick={() => setSelectedDate(date)}
                  className={`flex-shrink-0 flex flex-col items-center justify-center w-14 h-[72px] rounded-2xl transition-all duration-300 ${
                    isSelected
                      ? 'bg-sama-green text-white shadow-[0_0_20px_rgba(47,93,80,0.25)]'
                      : isToday
                        ? 'bg-white text-sama-charcoal border-2 border-sama-sage/40'
                        : 'bg-white text-sama-charcoal hover:bg-sama-light-sage/30 border border-sama-light-sage/50'
                  }`}
                  data-testid={`date-${index}`}
                >
                  <span className={`text-[10px] font-medium uppercase tracking-wider ${isSelected ? 'text-white/70' : 'text-sama-gray'}`}>
                    {format(date, 'EEE')}
                  </span>
                  <span className="text-lg font-bold mt-0.5">{format(date, 'd')}</span>
                  {isToday && !isSelected && (
                    <div className="w-1.5 h-1.5 rounded-full bg-sama-sage mt-0.5" />
                  )}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {/* ── Time Filters ────────────────────────────── */}
        <div className="flex gap-2 mt-2 mb-8 overflow-x-auto scrollbar-hide">
          {TIME_FILTERS.map((tf) => {
            const isActive = selectedTimeFilter === tf.id;
            let relevantSessions = sessions;
            if (classFilter) {
              const f = classFilter.toLowerCase();
              relevantSessions = relevantSessions.filter(s => (s.class_type?.name || '').toLowerCase().includes(f));
            }
            const count = tf.id === 'full_day'
              ? relevantSessions.length
              : relevantSessions.filter(s => {
                  const h = parseISO(s.start_time).getHours();
                  return h >= tf.minHour && h < tf.maxHour;
                }).length;

            return (
              <button
                key={tf.id}
                onClick={() => setSelectedTimeFilter(tf.id)}
                className={`flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  isActive
                    ? 'bg-sama-green text-white shadow-button'
                    : 'bg-white text-sama-gray border border-sama-light-sage hover:border-sama-sage hover:text-sama-charcoal'
                }`}
                data-testid={`time-filter-${tf.id}`}
              >
                {tf.label}
                {count > 0 && !isLoading && (
                  <span className={`ml-1.5 text-xs ${isActive ? 'text-white/70' : 'text-sama-sage'}`}>({count})</span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Sessions List ───────────────────────────── */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="rounded-2xl bg-white/60 border border-sama-light-sage/30 p-5" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="flex justify-between items-start">
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-16 bg-sama-light-sage/50 rounded-lg" />
                    <Skeleton className="h-5 w-40 bg-sama-light-sage/40 rounded-lg" />
                    <Skeleton className="h-3 w-28 bg-sama-light-sage/30 rounded-lg" />
                  </div>
                  <Skeleton className="h-9 w-16 bg-sama-light-sage/40 rounded-full" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredSessions.length === 0 ? (
          /* ── Empty State ────────────────────────────── */
          <div className="text-center py-16 px-4" data-testid="empty-state">
            <p className="text-3xl mb-4">&#127807;</p>
            {sessions.length > 0 && selectedTimeFilter !== 'full_day' ? (
              <>
                <h3 className="font-heading text-xl text-sama-charcoal mb-2">No classes in this window</h3>
                <p className="text-sama-gray text-sm mb-8">
                  No sessions in the {TIME_FILTERS.find(t => t.id === selectedTimeFilter)?.label.toLowerCase() || ''} at this studio. Try another time or view all.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => setSelectedTimeFilter('full_day')}
                    className="px-6 py-3 rounded-full bg-sama-green text-white font-medium shadow-button hover:shadow-elevated transition-all"
                    data-testid="view-full-day-btn"
                  >
                    View Full Day
                  </button>
                  <button
                    onClick={() => setShowStudioSheet(true)}
                    className="px-6 py-3 rounded-full border border-sama-sage text-sama-green font-medium hover:bg-sama-light-sage/30 transition-all"
                    data-testid="explore-studios-btn"
                  >
                    Explore Other Studios
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="font-heading text-xl text-sama-charcoal mb-2">A slower day at SAMA</h3>
                <p className="text-sama-gray text-sm mb-8">
                  No sessions scheduled at this studio today.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => {
                      const tomorrow = addDays(new Date(), 1);
                      setSelectedDate(tomorrow);
                    }}
                    className="px-6 py-3 rounded-full bg-sama-green text-white font-medium shadow-button hover:shadow-elevated transition-all"
                    data-testid="view-tomorrow-btn"
                  >
                    View Tomorrow
                  </button>
                  <button
                    onClick={() => setShowStudioSheet(true)}
                    className="px-6 py-3 rounded-full border border-sama-sage text-sama-green font-medium hover:bg-sama-light-sage/30 transition-all"
                    data-testid="explore-studios-btn-2"
                  >
                    Explore Other Studios
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          /* ── Class Cards with Time Sidebar ─────────── */
          <div className="space-y-0">
            {(() => {
              // Group sessions by hour for the timeline
              let lastHourLabel = '';
              return filteredSessions.map((session, idx) => {
                const ct = session.class_type || {};
                const isBooked = bookedSessionIds.has(session.id);
                const isFull = session.available_spots <= 0;
                const isBooking = bookingInProgress === session.id;
                const isJoining = waitlistInProgress === session.id;
                const wlPos = waitlistPositions[session.id];
                const subtext = getClassSubtext(ct);
                const sessionTime = parseISO(session.start_time);
                const isPast = sessionTime < new Date();
                // Members can only book/waitlist within the next BOOKING_WINDOW_DAYS days.
                // Admins bypass this so they can manage future schedules from the same view.
                const isBeyondBookingWindow = !memberIsAdmin && !isWithinBookingWindow(session.start_time);
                // Package format-restriction check — grey out any card
                // whose class_type isn't in the member's covered list.
                // Admins bypass so studio staff can still see/manage
                // every session from the same view.
                const isNotInPackage = !memberIsAdmin
                  && packageRestriction.restricted
                  && !!ct.id
                  && !packageRestriction.class_type_ids.includes(ct.id);
                const hourLabel = format(sessionTime, 'h a');
                const showHourLabel = hourLabel !== lastHourLabel;
                if (showHourLabel) lastHourLabel = hourLabel;

                {/* Spots info */}
                const maxCap = session.capacity || session.max_capacity || ct.max_capacity || 12;
                const booked = session.booked_count ?? (maxCap - (session.available_spots ?? maxCap));
                const spotsLeft = session.available_spots ?? (maxCap - booked);
                const fillPct = Math.min(100, Math.round((booked / maxCap) * 100));

                // Class-type overlay: cover image + description teaser +
                // category-emoji fallback so the card never renders a
                // broken photo when admin hasn't uploaded one yet.
                //
                // The description shown on the card is the FIRST 100
                // characters of the SAME text the "Know more" modal
                // renders (`full_description`), so members see a real
                // teaser of the modal content instead of a separate
                // `short_description` overlay field that could drift.
                // Fallback chain: full_description → short_description
                //   → ct.description (DB base).
                const overlay = classTypeOverlays[ct?.id] || {};
                const coverImg = overlay.cover_image_url;
                const rawDesc = overlay.full_description || overlay.short_description || ct?.description || '';
                const shortDesc = rawDesc.length > 100
                  ? rawDesc.slice(0, 100).trimEnd() + '…'
                  : rawDesc;
                const categoryEmoji = ({
                  pilates: '🤸', aerial: '🎪', yoga: '🧘', mst: '💪',
                  'mindful strength': '💪', restorative: '🌙',
                  meditation: '🕯️', workshop: '✨', 'spl workshops': '✨',
                }[(ct?.category || '').toLowerCase()] || '🧘');

                // Duration display — start·end and total minutes.
                const endTime = session.end_time ? new Date(session.end_time) : null;
                const durationMin = endTime
                  ? Math.max(1, Math.round((endTime - sessionTime) / 60000))
                  : (ct?.duration_minutes || null);
                const timeRangeLabel = endTime
                  ? `${format(sessionTime, 'h:mm a')} – ${format(endTime, 'h:mm a')}${durationMin ? ` · ${durationMin} min` : ''}`
                  : format(sessionTime, 'h:mm a');

                // Studio location for the venue chip.
                const sessionStudio = studios.find(s => s.id === session.studio_id);

                return (
                  <div
                    key={session.id}
                    className="flex"
                    style={{ animation: `fade-in-up 0.4s ease-out ${idx * 60}ms both` }}
                    data-testid={`session-${session.id}`}
                  >

                    {/* Right: Card */}
                    <div className={`flex-1 mb-3 rounded-2xl overflow-hidden transition-all duration-300 ${
                      isPast
                        ? 'bg-stone-50/60 border border-stone-200/50 opacity-50'
                        : isBooked
                          ? 'bg-sama-green/5 border border-sama-green/20'
                          : 'bg-white/70 backdrop-blur-sm border border-sama-light-sage/40 hover:border-sama-sage/50 hover:shadow-card'
                    }`}>
                      <div className="p-4 sm:p-5 flex items-start gap-3">
                        {/* Class photo (or category-emoji fallback) */}
                        <div
                          className={`w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded-xl overflow-hidden bg-sama-light-sage/40 flex items-center justify-center ${isPast ? 'opacity-50' : ''}`}
                          data-testid={`class-photo-${session.id}`}
                        >
                          {coverImg ? (
                            <img
                              src={coverImg}
                              alt={ct.name || 'Class'}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          ) : (
                            <span className="text-3xl" aria-hidden="true">{categoryEmoji}</span>
                          )}
                        </div>

                        {/* Class Info */}
                        <div className="flex-1 min-w-0">
                          {/* Time */}
                          <p className={`text-xs font-medium tracking-wider uppercase mb-1 ${isPast ? 'text-stone-400' : isBooked ? 'text-sama-green/60' : 'text-sama-sage'}`}>
                            {timeRangeLabel}
                          </p>
                          {/* Class Name */}
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className={`font-semibold text-base leading-snug ${isPast ? 'text-stone-400 line-through' : isBooked ? 'text-sama-green/70' : 'text-sama-charcoal'}`}>
                              {ct.name || 'Class'}
                            </h3>
                          </div>
                          {/* Short description — 2-line clamp so cards stay compact,
                              followed by a "Know more" affordance that opens the full details modal. */}
                          {shortDesc && (
                            <p
                              className={`text-xs mb-1 line-clamp-2 ${isPast ? 'text-stone-400' : isBooked ? 'text-sama-green/60' : 'text-sama-gray/90'}`}
                              data-testid={`class-desc-${session.id}`}
                            >
                              {shortDesc}
                            </p>
                          )}
                          {ct.id && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setKnowMoreClassId(ct.id); }}
                              className="text-[11px] uppercase tracking-wider text-sama-sage hover:text-sama-green font-medium mb-1"
                              data-testid={`know-more-${session.id}`}
                            >
                              Know more →
                            </button>
                          )}
                          {/* Instructor block hidden — surfaces only in Book modal */}
                          {/* Subtext */}
                          {subtext && (
                            <p className={`text-sm ${isBooked ? 'text-sama-green/50' : 'text-sama-gray'}`}>
                              {subtext}
                            </p>
                          )}
                          {/* Venue chip — deep-linked to Google Maps */}
                          {sessionStudio && (
                            <a
                              href={sessionStudio.google_maps_url ||
                                `https://www.google.com/maps/search/${encodeURIComponent(sessionStudio.name + ' ' + (sessionStudio.address || ''))}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="mt-1 inline-flex items-center gap-1 text-[11px] text-sama-sage hover:text-sama-green"
                              data-testid={`class-venue-${session.id}`}
                            >
                              <MapPinIcon className="w-3 h-3" />
                              <span className="truncate max-w-[220px]">{sessionStudio.name}</span>
                            </a>
                          )}
                          {/* Credits + spots row */}
                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            {ct?.credits_required != null && (
                              <span className="text-[11px] px-2 py-0.5 rounded-full bg-sama-sage/10 text-sama-sage font-medium" data-testid={`class-credits-${session.id}`}>
                                {ct.credits_required} credit{ct.credits_required === 1 ? '' : 's'}
                              </span>
                            )}
                            <div className="flex items-center gap-2">
                              <div className="flex-1 max-w-[80px] h-1.5 rounded-full bg-sama-light-sage/40 overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all duration-500 ${
                                    fillPct >= 90 ? 'bg-red-400/70' : fillPct >= 60 ? 'bg-amber-400/70' : 'bg-sama-sage/60'
                                  }`}
                                  style={{ width: `${fillPct}%` }}
                                />
                              </div>
                              <span className={`text-xs ${spotsLeft <= 2 && spotsLeft > 0 ? 'text-red-500 font-medium' : spotsLeft <= 0 ? 'text-amber-600 font-medium' : 'text-sama-gray/70'}`} data-testid={`spots-${session.id}`}>
                                {spotsLeft > 0
                                  ? `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`
                                  : `Waitlist ${wlPos ? `#${wlPos}` : 'open'}`}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* CTA */}
                        <div className="flex-shrink-0">
                          {isPast ? (
                            <span className="px-4 py-2.5 rounded-full bg-stone-100 text-stone-400 text-sm font-medium" data-testid={`ended-${session.id}`}>
                              Ended
                            </span>
                          ) : isBeyondBookingWindow ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); toast.info(BOOKING_WINDOW_TOAST, { duration: 5000 }); }}
                              className="px-4 py-2.5 rounded-full bg-stone-100 text-stone-500 text-sm font-medium hover:bg-stone-200 transition-colors"
                              data-testid={`not-open-${session.id}`}
                            >
                              Opens soon
                            </button>
                          ) : isNotInPackage ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toast.info(
                                  `${ct.name || 'This class'} isn't included in your ${packageRestriction.package_names.join(' + ') || 'package'}. Upgrade or top up to book it.`,
                                  { duration: 6000 },
                                );
                              }}
                              className="px-4 py-2.5 rounded-full bg-stone-100 text-stone-500 text-sm font-medium hover:bg-stone-200 transition-colors"
                              data-testid={`not-in-package-${session.id}`}
                              title={`Not covered by your package: ${packageRestriction.package_names.join(', ') || 'current pack'}`}
                            >
                              Not in your pack
                            </button>
                          ) : isBooked ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); setShowCancelConfirm(session); }}
                              className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-sama-green/10 text-sama-green text-sm font-medium transition-colors hover:bg-rose-50 hover:text-rose-500 group"
                              data-testid={`cancel-booking-btn-${session.id}`}
                            >
                              <Check className="w-4 h-4 group-hover:hidden" />
                              <X className="w-4 h-4 hidden group-hover:block" />
                              <span className="group-hover:hidden">Booked</span>
                              <span className="hidden group-hover:inline">Cancel</span>
                            </button>
                          ) : isFull ? (
                            wlPos ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowWaitlistCancelConfirm(session);
                                }}
                                disabled={waitlistCancelInProgress === session.id}
                                className="group px-4 py-2.5 rounded-full bg-amber-50 text-amber-700 text-sm font-medium hover:bg-rose-50 hover:text-rose-600 transition-colors"
                                data-testid={`cancel-waitlist-btn-${session.id}`}
                              >
                                {waitlistCancelInProgress === session.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <span className="group-hover:hidden">Waitlist #{wlPos}</span>
                                    <span className="hidden group-hover:inline">Cancel</span>
                                  </>
                                )}
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Open the same details modal in
                                  // waitlist mode instead of silently
                                  // adding the member. They see class
                                  // details + an explicit "Join Waitlist"
                                  // CTA. handleClassClick handles the
                                  // non-preferred-studio nudge too, then
                                  // routes to waitlist mode below.
                                  handleClassClick(session);
                                }}
                                disabled={isJoining}
                                className="px-4 py-2.5 rounded-full bg-sama-light-sage/50 text-sama-gray text-sm font-medium hover:bg-sama-light-sage transition-colors"
                                data-testid={`waitlist-btn-${session.id}`}
                              >
                                {isJoining ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Waitlist'}
                              </button>
                            )
                          ) : (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleClassClick(session); }}
                              disabled={isBooking}
                              className="px-5 py-2.5 rounded-full bg-sama-green text-white text-sm font-medium shadow-button hover:shadow-elevated active:scale-[0.97] transition-all"
                              data-testid={`book-btn-${session.id}`}
                            >
                              {isBooking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Book'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>

      {/* ── Modals ────────────────────────────────────── */}
      <BookingConfirmationModal
        session={selectedSession}
        studioName={currentStudio?.name}
        isOpen={showBookingModal}
        onClose={() => { setShowBookingModal(false); setSelectedSession(null); }}
        onConfirm={handleConfirmBooking}
        isLoading={bookingInProgress !== null}
        userCredits={creditBalance}
        isBooked={selectedSession ? bookedSessionIds.has(selectedSession.id) : false}
        // Switch modal to waitlist UX when the class is full (and not
        // already booked by this member). Keeps the same details card,
        // just swaps the primary CTA + footer copy.
        mode={(() => {
          if (!selectedSession) return 'book';
          if (bookedSessionIds.has(selectedSession.id)) return 'book';
          const available =
            typeof selectedSession.available_spots === 'number'
              ? selectedSession.available_spots
              : (selectedSession.capacity || 0) - (selectedSession.booked_count || 0);
          return available <= 0 ? 'waitlist' : 'book';
        })()}
      />
      <GuestCheckoutModal
        isOpen={showGuestCheckout}
        onClose={() => { setShowGuestCheckout(false); setGuestSession(null); }}
        session={guestSession}
        onSuccess={(result) => {
          setShowGuestCheckout(false); setGuestSession(null);
          navigate(`/booking-confirmed?booking=${result.booking_id}`);
        }}
      />

      {/* Non-Preferred-Studio Booking Confirmation
          Shown once when a member tries to book at a studio that
          isn't their preferred one. "I understand" proceeds to the
          regular booking modal; closing/cancelling backs out cleanly. */}
      {studioMismatchSession && (() => {
        const targetStudioId =
          studioMismatchSession.studio_id ||
          studioMismatchSession.studio?.id ||
          selectedStudio;
        const targetStudioName =
          studioMismatchSession.studio?.name ||
          studios.find(s => s.id === targetStudioId)?.name ||
          currentStudio?.name ||
          'this studio';
        const preferredStudioName =
          studios.find(s => s.id === member?.preferred_studio_id)?.name ||
          'your preferred studio';

        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="studio-mismatch-modal">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setStudioMismatchSession(null)}
            />
            <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6">
              <h3 className="font-heading text-xl text-foreground mb-3">
                Different studio?
              </h3>
              <p className="text-sm text-muted-foreground mb-2">
                You are booking a class at{' '}
                <strong className="text-foreground">{targetStudioName}</strong>,
                which is not your preferred studio
                {preferredStudioName !== 'your preferred studio' && (
                  <> (<strong className="text-foreground">{preferredStudioName}</strong>)</>
                )}
                . Are you sure you want to go ahead?
              </p>
              <div className="flex gap-2 mt-5">
                <button
                  type="button"
                  onClick={() => setStudioMismatchSession(null)}
                  data-testid="studio-mismatch-cancel"
                  className="flex-1 h-11 rounded-full border border-sama-charcoal/20 text-sm font-medium text-sama-charcoal hover:bg-sama-charcoal/5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const s = studioMismatchSession;
                    setStudioMismatchSession(null);
                    openBookingModalForSession(s);
                  }}
                  data-testid="studio-mismatch-confirm"
                  className="flex-1 h-11 rounded-full bg-sama-green text-white text-sm font-medium hover:bg-sama-green/90 transition-colors"
                >
                  I understand
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Cancel Booking Confirmation */}
      {showCancelConfirm && (() => {
        const info = bookedSessionMap[showCancelConfirm.id];
        const startTime = info?.start_time || showCancelConfirm.start_time;
        let hoursUntil = null;
        if (startTime) try { hoursUntil = (new Date(startTime) - new Date()) / 3600000; } catch { /* noop */ }
        const withinWindow = hoursUntil !== null && hoursUntil < cancelWindowHours;
        const isCancelling = cancelInProgress === showCancelConfirm.id;

        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !isCancelling && setShowCancelConfirm(null)} />
            <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6" data-testid="cancel-confirm-dialog">
              <h3 className="font-heading text-xl text-foreground mb-3">Cancel Booking?</h3>
              <p className="text-sm text-muted-foreground mb-1">
                <strong>{showCancelConfirm.class_type?.name || 'Class'}</strong>
              </p>
              {withinWindow ? (
                <div className="flex items-start gap-2 p-3 bg-rose-50 rounded-xl mb-4">
                  <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-rose-800" data-testid="cancellation-window-closed-msg">
                    The cancellation window of {cancelWindowHours} hours has passed.
                    A late cancellation does not give enough time for other members
                    to plan their schedule and attend the class. Cancellation and
                    credit reversal is not possible for this class.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-green-700 bg-green-50 rounded-xl p-3 mb-4">
                  Your <strong>{info?.credits_charged || 0} credit{(info?.credits_charged || 0) !== 1 ? 's' : ''}</strong> will be refunded.
                </p>
              )}
              <div className="flex flex-col gap-2">
                {withinWindow ? (
                  <button
                    onClick={() => setShowCancelConfirm(null)}
                    className="w-full py-3 rounded-full bg-stone-900 text-white font-medium hover:bg-stone-800 transition-all"
                    data-testid="cancel-window-closed-close-btn"
                  >
                    Got it
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleCancelBooking(showCancelConfirm)}
                      disabled={isCancelling}
                      className="w-full py-3 rounded-full bg-rose-500 text-white font-medium hover:bg-rose-600 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                      data-testid="confirm-cancel-btn"
                    >
                      {isCancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Yes, Cancel Booking'}
                    </button>
                    <button
                      onClick={() => setShowCancelConfirm(null)}
                      disabled={isCancelling}
                      className="w-full py-3 rounded-full border border-stone-200 text-foreground font-medium hover:bg-stone-50 transition-all"
                      data-testid="keep-booking-btn"
                    >
                      Keep Booking
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Waitlist cancel confirmation modal — spec mandates a "Cancel Waitlist?"
          confirmation with Confirm + Cancel options. Mirrors the same modal
          style as the booking-cancel flow. */}
      {showWaitlistCancelConfirm && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" data-testid="waitlist-cancel-modal">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => waitlistCancelInProgress ? null : setShowWaitlistCancelConfirm(null)}
          />
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="font-heading text-xl text-foreground mb-2">
              Cancel Waitlist?
            </h3>
            <p className="text-sm text-muted-foreground mb-5">
              You will be removed from the waitlist for this class.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setShowWaitlistCancelConfirm(null)}
                disabled={!!waitlistCancelInProgress}
                className="py-3 rounded-full border border-border text-foreground font-medium hover:bg-muted/40 transition-all disabled:opacity-50"
                data-testid="waitlist-cancel-modal-keep"
              >
                Keep
              </button>
              <button
                onClick={() => handleLeaveWaitlist(showWaitlistCancelConfirm)}
                disabled={!!waitlistCancelInProgress}
                className="py-3 rounded-full bg-rose-600 text-white font-medium hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center"
                data-testid="waitlist-cancel-modal-confirm"
              >
                {waitlistCancelInProgress ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Membership Start Date block — surfaced when the booking or waitlist
          API rejects with code='membership_not_started'. Spec mandates a
          simple OK modal; nothing to do here besides acknowledge it. */}
      {membershipBlock && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" data-testid="membership-start-block-modal">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMembershipBlock(null)}
          />
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="font-heading text-xl text-foreground mb-2">
              Membership Start Date Restriction
            </h3>
            <p className="text-sm text-muted-foreground mb-5" data-testid="membership-start-block-message">
              {membershipBlock}
            </p>
            <button
              onClick={() => setMembershipBlock(null)}
              className="w-full py-3 rounded-full bg-foreground text-background font-medium hover:opacity-90 transition-all"
              data-testid="membership-start-block-ok"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Instructor Bio popup */}
      {viewBioInstructorId && (
        <InstructorBioModal id={viewBioInstructorId} onClose={() => setViewBioInstructorId(null)} />
      )}
      {/* Class Know-More popup */}
      {knowMoreClassId && (
        <ClassKnowMoreModal id={knowMoreClassId} onClose={() => setKnowMoreClassId(null)} />
      )}
    </div>
  );
}

// ─── Instructor Bio modal ──────────────────────────────────────────
// z-index 130 keeps it above the shadcn Dialog booking modal (z-50 in
// the built-in overlay). Users open this from the "View bio" link
// INSIDE the booking modal — the bio must layer on top with its own
// close button, otherwise clicking the backdrop shuts the booking
// modal too and loses the user's flow.
function InstructorBioModal({ id, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/instructor-content/${id}`)
      .then(r => r.json()).then(setData).catch(() => setData({ name: 'Instructor' }));
  }, [id]);
  return (
    <div className="fixed inset-0 z-[130] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="instructor-bio-modal">
      <div className="relative bg-white rounded-2xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close bio"
          className="absolute top-3 right-3 rounded-full p-1.5 text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors"
          data-testid="instructor-bio-close"
        >
          <X className="w-5 h-5" />
        </button>
        {!data ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <>
            <div className="flex items-center gap-4 pr-8">
              {data.profile_image ? (
                <img src={data.profile_image} alt={data.name} className="w-20 h-20 rounded-full object-cover" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-sama-light-sage/40 flex items-center justify-center text-2xl font-semibold text-sama-green">
                  {(data.name || '?').charAt(0)}
                </div>
              )}
              <h3 className="text-lg font-semibold" data-testid="instructor-bio-name">{data.name}</h3>
            </div>
            <p className="text-sm text-stone-700 whitespace-pre-wrap" data-testid="instructor-bio-text">
              {data.bio || 'This instructor has not added a bio yet.'}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Class "Know More" modal ───────────────────────────────────────
function ClassKnowMoreModal({ id, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/class-type-content/${id}`)
      .then(r => r.json()).then(setData).catch(() => setData({ name: 'Class' }));
  }, [id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="class-knowmore-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-3" onClick={e => e.stopPropagation()}>
        {!data ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <>
            <h3 className="text-lg font-semibold" data-testid="knowmore-title">{data.name}</h3>
            {data.category && <p className="text-xs uppercase tracking-wider text-sama-sage">{data.category}</p>}
            <p className="text-sm text-stone-700 whitespace-pre-wrap" data-testid="knowmore-description">
              {data.full_description || data.short_description || 'No description available yet.'}
            </p>
            {data.benefits?.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase text-stone-500 mt-2">Benefits</p>
                <ul className="list-disc pl-5 text-sm text-stone-700 mt-1 space-y-0.5">
                  {data.benefits.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            )}
            {data.format_details && (
              <div>
                <p className="text-xs font-semibold uppercase text-stone-500 mt-2">Format</p>
                <p className="text-sm text-stone-700">{data.format_details}</p>
              </div>
            )}
            <p className="text-xs text-muted-foreground pt-2">Duration: {data.duration_minutes || '—'} minutes</p>
          </>
        )}
      </div>
    </div>
  );
}
